import { IProductDetails } from "@Models/IProductDetails";
import { RcFile } from "antd/lib/upload";
import { AxiosError, AxiosResponse, Canceler } from "axios";
import { z } from "zod";
import { IMetadata, IMetadataRequest } from "./IMetadata";
import { HashedIdSchema } from "./schemas";

/**
 * @note
 * All types related to Response Payloads and Response Objects
 * will eventually be replaced with Zod schemas.For this to work effectively,
 * we need to switch to strict types and resolve all type errors.
 */

export interface IActivityDefinitions {
    products: IProductDetails[];
    categories: IActivityCategoryDefinition[];
    activities: IActivityDefinition[];
}

export interface IActivityCategoryDefinition {
    activityCategoryId: string;
    categoryName: string;
    label: string;
    parentCategoryId: string;
    productId: string;
}

export interface IActivityDefinition {
    activityCategoryId: string;
    activityId: string;
    name: string;
    label: string;
    trackingIds: number;
}

/**
 * Schema for the comment details.
 * This is part of the `IActivityDetailsSchema`
 */
export const ICommentDetailsSchema = z.object({
    activityCommentId: HashedIdSchema.optional(),
    activityInstanceId: HashedIdSchema,
    content: z.string(),
    dateTime: z.string().datetime(),
    fullName: z.string(),
    parentActivityCommentId: HashedIdSchema,
    time: z.number().int(),
    userId: HashedIdSchema,
});

/**
 * Type for the comment details.
 */
export type ICommentDetails = z.infer<typeof ICommentDetailsSchema>;

/**
 * Schema for the activity meta details.
 * This is part of the `IActivityDetailsSchema`
 */
export const IActivityMetaDetailsSchema = z
    .object({
        activityMetaId: HashedIdSchema,
        activityInstanceId: HashedIdSchema,
        description: z.string().optional(),
        isCMEPublic: z.boolean().optional(),
        questionnaireId: z.string().optional(),
        isPublic: z.boolean().optional(),
        tags: z.array(z.string()).optional(),
        title: z.string().optional(),
    })
    .optional();

/**
 * Schema for the activity metric details.
 * This is part of the `IActivityDetailsSchema`
 */
export const IActivityMetricDetailsSchema = z.object({
    activityMetricId: HashedIdSchema,
    name: z.string(),
    quantile: z.number().optional(),
    trackingId: z.number().int(),
    value: z.number(),
    valueMedian: z.number(),
});

export type IActivityMetricDetails = z.infer<typeof IActivityMetricDetailsSchema>;

/**
 * Schema for the signature item.
 */
export const ISignatureItemSchema = z.object({
    metric: z.string(),
    timeIndex: z.number(),
    trackingId: z.number(),
    value: z.number(),
});

/**
 * Type for the signature item.
 */
export type ISignatureItem = z.infer<typeof ISignatureItemSchema>;

/**
 * Schema for the activity details.
 * This is a Response Object that represents the activity details.
 */
export const IActivityDetailsSchema = z.object({
    activityCategoryId: HashedIdSchema,
    activityComments: z.array(ICommentDetailsSchema).optional(),
    activityGuid: z.string().uuid(),
    activityId: HashedIdSchema,
    activityInstanceId: HashedIdSchema,
    activityLabel: z.string(),
    activityMeta: IActivityMetaDetailsSchema.optional(),
    activityMetrics: z.array(IActivityMetricDetailsSchema).optional(),
    activityName: z.string(),
    activitySignature: z.array(ISignatureItemSchema).optional(),
    campaignId: HashedIdSchema.optional(),
    categoryLabel: z.string(),
    categoryName: z.string(),
    creationDate: z.string().datetime(),
    duration: z.number().int(),
    durationMedian: z.number().int(),
    durationQuantile: z.number(),
    mediaStatus: z.enum(["complete", "created", "pending", "processing", "error"]).optional(),
    mediaUrl: z.string().optional(),
    points: z.number().int(),
    thumbnailUrl: z.string().optional(),
    trackingIds: z.number().int(),
    userFullName: z.string(),
    userId: HashedIdSchema,
});

/**
 * Type for the activity details.
 * This is a Response Object that represents the activity details.
 */
export type IActivityDetails = z.infer<typeof IActivityDetailsSchema>;

/**
 * Schema for the activity payload.
 */
export const IActivityPayloadSchema = z.object({
    activities: z.array(IActivityDetailsSchema),
    nextPage: z.number().int(),
});

/**
 * Type for the activity payload.
 */
export interface IActivityPayload {
    activities: IActivityDetails[];
    nextPage: number;
}

export interface IUserActivityMinimal {
    activityId: string;
    creationDate: Date;
}

export interface IActivityCreationDetails {
    activityName: string;
    activityGuid: string;
    dataParts?: number;
    dataFormat?: string;
    mediaParts: number;
    mediaFormat?: string;
    campaignGuid?: string;
    duration: number;
    userId: string;
    creationDate: string;
    deviceId: string;
}

export const IUploadDetailsSchema = z.object({
    partNumber: z.number().int(),
    uploadId: z.string(),
    url: z.string().url(),
});

export type IUploadDetails = z.infer<typeof IUploadDetailsSchema>;

export const IActivityCreationResultSchema = z.object({
    data: z.array(IUploadDetailsSchema),
    id: HashedIdSchema,
    media: z.array(IUploadDetailsSchema),
});

export type IActivityCreationResult = z.infer<typeof IActivityCreationResultSchema>;

export interface IPartETag {
    eTag: string;
    partNumber: number;
}

export interface IAbortedUpload {
    abort: Canceler;
    error: AxiosResponse | Error;
}

export const IMediaServiceUploadResponseSchema = z.object({
    headers: z.object({
        // we are not using the ETag schema here in case ETags change in the future
        etag: z.string(),
    }),
});

export type IStoredUploads = Record<string, IPendingUpload>;

export type IUploadStatus = "aborted" | "completed" | "created" | "initiated" | "idle" | "pending" | "error";

export interface IPendingUpload {
    activityInstanceId: string;
    eTags: Map<number, string>;
    fileDetails: Pick<RcFile, "lastModified" | "name" | "size" | "type">;
    media: IUploadDetails[];
    status: IUploadStatus;
    totalParts: number;
    uploadId: string;
}

export interface IUploadResultDetails {
    action: "abort" | "complete";
    fileFormat: string;
    parts: IPartETag[];
    type: "data" | "media";
    uploadId: string;
}

export interface IUploadedDetails {
    id: string;
    action: "abort" | "complete";
    uploadId: string;
}

export interface IActivityMetaDetails {
    activityMetaId: string;
    activityInstanceId: string;
    description?: string;
    isPublic?: boolean;
    isCMEPublic?: boolean;
    questionnaireId?: string;
    tags?: string[];
    title?: string;
}

export interface IActivityExportMetadata extends IMetadata {
    exportMetadataId: string;
}

export interface IActivityExportMetadataRequest extends IMetadataRequest {
    activityInstanceIds: string[];
}
